/**
 * JSON Content Renderer
 * Renders page content from JSON using the Universal Renderer
 * 
 * Color Palette:
 * - Navy Primary: #1a3a52
 * - Light Navy: #2d5f7e
 * - Accent Gold: #d4a574
 * - Gold Dark: #c08f5c
 * - Success Green: #2d7a4f
 * - Text: #1a3a52
 * - Text Light: #666666
 * - Background: #fafafa
 * - Border: #e0e0e0
 */

import React from 'react';
import { useTranslation } from '../hooks/useTranslation';

// Color constants for consistency
const COLORS = {
  navyPrimary: '#1a3a52',
  lightNavy: '#2d5f7e',
  accentGold: '#d4a574',
  goldDark: '#c08f5c',
  successGreen: '#2d7a4f',
  text: '#1a3a52',
  textLight: '#666666',
  background: '#fafafa',
  border: '#e0e0e0',
  white: '#ffffff',
  // Derived colors for variants
  dangerRed: '#c0392b',
  dangerLight: '#e74c3c',
  warningAmber: '#d4a574',
  infoBlue: '#2d5f7e',
};

const JsonContentRenderer = ({ jsonData }) => {
  const t = useTranslation();
  
  if (!jsonData || !jsonData.sections) {
    return <div>{t('sidebar.invalidJsonData', 'Invalid JSON data')}</div>;
  }

  return (
    <div className="json-content-renderer">
      <style>{getStyles()}</style>
      {jsonData.sections.map((section, index) => (
        <RenderComponent key={section.id || index} component={section} />
      ))}
    </div>
  );
};

// Component router
const RenderComponent = ({ component }) => {
  switch (component.type) {
    case 'hero':
      return <Hero data={component} />;
    case 'section':
      return <Section data={component} />;
    case 'callout':
      return <Callout data={component} />;
    case 'card-grid':
      return <CardGrid data={component} />;
    case 'text-block':
      return <TextBlock data={component} />;
    case 'list':
      return <List data={component} />;
    default:
      console.warn(`Unknown component type: ${component.type}`);
      return null;
  }
};

// Hero Component
const Hero = ({ data }) => {
  const content = data.content || {};
  
  return (
    <div 
      className="json-hero"
      style={{
        background: `linear-gradient(135deg, ${COLORS.navyPrimary} 0%, ${COLORS.lightNavy} 100%)`,
        padding: '48px 40px',
        borderRadius: '16px',
        marginBottom: '24px',
        textAlign: 'center',
        color: COLORS.white,
        boxShadow: '0 8px 32px rgba(26, 58, 82, 0.25)'
      }}
    >
      <h1 style={{ 
        fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
        margin: '0 0 16px 0',
        fontWeight: 700,
        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
        letterSpacing: '-0.02em',
        color: COLORS.accentGold,
      }}>
        {content.title}
      </h1>
      {content.subtitle && (
        <p style={{ 
          fontSize: 'clamp(1rem, 2vw, 1.2rem)', 
          margin: 0,
          opacity: 0.95,
          fontWeight: 400,
          maxWidth: '700px',
          marginLeft: 'auto',
          marginRight: 'auto',
          lineHeight: 1.6,
          color: COLORS.accentGold,
        }}>
          {content.subtitle}
        </p>
      )}
      {content.badge && (
        <div style={{
          display: 'inline-block',
          marginTop: '20px',
          padding: '10px 24px',
          background: `linear-gradient(135deg, ${COLORS.accentGold}, ${COLORS.goldDark})`,
          borderRadius: '24px',
          fontSize: '0.9rem',
          fontWeight: 600,
          color: COLORS.white,
          boxShadow: '0 4px 12px rgba(212, 165, 116, 0.4)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {content.badge.icon && <span style={{ marginRight: '8px' }}>{content.badge.icon}</span>}
          {content.badge.text}
        </div>
      )}
    </div>
  );
};

// Section Component
const Section = ({ data }) => {
  const content = data.content || {};
  const styling = data.styling || {};
  
  const isDark = styling.theme === 'dark';
  
  const sectionStyle = {
    background: isDark ? COLORS.navyPrimary : COLORS.white,
    color: isDark ? COLORS.white : COLORS.text,
    padding: '32px',
    borderRadius: '12px',
    marginBottom: '24px',
    boxShadow: isDark ? '0 4px 16px rgba(26, 58, 82, 0.25)' : '0 4px 16px rgba(26, 58, 82, 0.08)',
    textAlign: styling.textAlign || 'left',
    border: isDark ? 'none' : `1px solid ${COLORS.border}`
  };

  if (styling.borders) {
    const side = styling.borders.side || 'left';
    sectionStyle[`border${side.charAt(0).toUpperCase() + side.slice(1)}`] = 
      `${styling.borders.width || '4px'} solid ${styling.borders.color || COLORS.accentGold}`;
  }

  return (
    <div style={sectionStyle} className={isDark ? 'json-section-dark' : ''}>
      {content.header && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: styling.textAlign === 'center' ? 'center' : 'flex-start',
          gap: '14px', 
          marginBottom: '24px' 
        }}>
          {content.header.icon && (
            <span style={{
              width: '48px',
              height: '48px',
              background: isDark 
                ? `linear-gradient(135deg, ${COLORS.accentGold}, ${COLORS.goldDark})`
                : `linear-gradient(135deg, ${COLORS.navyPrimary}, ${COLORS.lightNavy})`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.4rem',
              boxShadow: isDark 
                ? '0 4px 12px rgba(212, 165, 116, 0.3)'
                : '0 4px 12px rgba(26, 58, 82, 0.2)'
            }}>
              {content.header.icon}
            </span>
          )}
          <h2 style={{ 
            fontSize: isDark ? '1.4rem' : '1.6rem', 
            margin: 0, 
            fontWeight: 600,
            color: isDark ? COLORS.accentGold : COLORS.text,
            letterSpacing: '-0.01em'
          }}>
            {content.header.title}
          </h2>
        </div>
      )}
      
      {content.body && content.body.map((child, idx) => (
        <RenderComponent key={idx} component={child} />
      ))}
    </div>
  );
};

// Callout Component with improved contrast
const Callout = ({ data }) => {
  const content = data.content || {};
  const variant = data.variant || 'info';
  
  // Improved color schemes with better contrast
  const variantStyles = {
    info: { 
      background: `linear-gradient(135deg, ${COLORS.navyPrimary}, ${COLORS.lightNavy})`,
      textColor: COLORS.white,
      markerColor: COLORS.accentGold
    },
    success: { 
      background: `linear-gradient(135deg, ${COLORS.successGreen}, #3d9465)`,
      textColor: COLORS.white,
      markerColor: '#a8e6cf'
    },
    warning: { 
      background: `linear-gradient(135deg, #f8f4e8, #f5eed8)`,
      textColor: COLORS.text,
      borderColor: COLORS.accentGold,
      markerColor: COLORS.goldDark
    },
    danger: { 
      background: `linear-gradient(135deg, #fdf2f2, #fce8e8)`,
      textColor: COLORS.text,
      borderColor: COLORS.dangerRed,
      markerColor: COLORS.dangerRed
    }
  };
  
  const style = variantStyles[variant] || variantStyles.info;
  const isLightBg = variant === 'warning' || variant === 'danger';
  
  // Determine CSS class based on variant
  const getCalloutClass = () => {
    if (!isLightBg) return 'json-callout-dark';
    if (variant === 'danger') return 'json-callout-light-danger';
    if (variant === 'warning') return 'json-callout-light-warning';
    return '';
  };
  const calloutClass = getCalloutClass();
  
  return (
    <div style={{
      background: style.background,
      color: style.textColor,
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '20px',
      borderLeft: style.borderColor ? `4px solid ${style.borderColor}` : 'none',
      boxShadow: isLightBg ? '0 2px 8px rgba(26, 58, 82, 0.06)' : '0 4px 16px rgba(26, 58, 82, 0.15)'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px', 
        marginBottom: '12px' 
      }}>
        {content.icon && <span style={{ fontSize: '1.3rem' }}>{content.icon}</span>}
        <h3 style={{ 
          margin: 0, 
          fontSize: '1.15rem', 
          fontWeight: 600,
          color: style.textColor,
          letterSpacing: '-0.01em'
        }}>
          {content.title}
        </h3>
      </div>
      
      {content.body && (
        <div 
          className={calloutClass}
          style={{ 
            lineHeight: 1.7, 
            marginBottom: content.list ? '12px' : 0,
            ...(isLightBg ? { color: style.textColor } : {})
          }}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(content.body) }}
        />
      )}
      
      {content.list && (
        <ul 
          className={calloutClass}
          style={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: '12px 0 0 0' 
          }}
        >
          {content.list.map((item, idx) => (
            <li 
              key={idx} 
              style={{ 
                padding: '8px 0 8px 28px', 
                position: 'relative',
                lineHeight: 1.6,
                ...(isLightBg ? { color: style.textColor } : {})
              }}
              dangerouslySetInnerHTML={{ 
                __html: `<span style="position: absolute; left: 0; font-weight: bold; color: ${style.markerColor};">✓</span>${parseMarkdown(item)}` 
              }}
            />
          ))}
        </ul>
      )}
      
      {content.nested && content.nested.map((child, idx) => (
        <div key={idx} style={{ marginTop: '16px' }}>
          <RenderComponent component={child} />
        </div>
      ))}
      
      {content.link && (
        <div style={{ marginTop: '16px' }}>
          <a
            href={content.link.url}
            download={content.link.url.includes('.zip') || content.link.url.includes('.pdf')}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: isLightBg ? COLORS.navyPrimary : COLORS.white,
              color: isLightBg ? COLORS.white : COLORS.navyPrimary,
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            {content.link.text}
          </a>
        </div>
      )}
    </div>
  );
};

// CardGrid Component
const CardGrid = ({ data }) => {
  const content = data.content || {};
  const layout = data.layout || {};
  
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: layout.columns || 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: layout.gap || '24px',
      marginBottom: '24px'
    }}>
      {content.cards && content.cards.map((card, idx) => (
        <Card key={idx} card={card} variant={data.variant} />
      ))}
    </div>
  );
};

// Card Component
const Card = ({ card, variant }) => {
  const styling = card.styling || {};
  const hasGradientBg = styling.gradient;
  
  // Determine text color: custom color takes precedence, then white for gradients, then default
  const textColor = styling.color || (hasGradientBg ? COLORS.white : COLORS.text);
  const textColorLight = styling.color || (hasGradientBg ? 'rgba(255,255,255,0.9)' : COLORS.textLight);
  
  let cardStyle = {
    background: COLORS.white,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 16px rgba(26, 58, 82, 0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: `1px solid ${COLORS.border}`,
    color: textColor
  };
  
  if (styling.borders) {
    const side = styling.borders.side || 'left';
    const borderKey = `border${side.charAt(0).toUpperCase() + side.slice(1)}`;
    cardStyle[borderKey] = `${styling.borders.width || '4px'} solid ${styling.borders.color || COLORS.accentGold}`;
  }
  
  if (styling.gradient) {
    cardStyle.background = `linear-gradient(135deg, ${styling.gradient.from}, ${styling.gradient.to})`;
    cardStyle.color = textColor;
    cardStyle.border = 'none';
  }
  
  return (
    <div style={cardStyle} className={`json-card ${hasGradientBg ? 'json-card-gradient' : ''}`}>
      {card.header && (
        <div style={{ marginBottom: '16px' }}>
          {card.header.badge && (
            <span style={{
              display: 'inline-block',
              background: `linear-gradient(135deg, ${COLORS.navyPrimary}, ${COLORS.lightNavy})`,
              color: COLORS.white,
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.95rem',
              fontWeight: 600,
              marginRight: '12px',
              marginBottom: '12px',
              boxShadow: '0 2px 8px rgba(26, 58, 82, 0.2)'
            }}>
              {card.header.badge.text}
            </span>
          )}
          {card.header.title && (
            <h3 style={{ 
              fontSize: '1.25rem', 
              margin: '8px 0 0 0', 
              fontWeight: 600,
              color: textColor,
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {card.header.icon && (
                <span style={{ fontSize: '1.3em' }}>
                  {card.header.icon}
                </span>
              )}
              {card.header.title}
            </h3>
          )}
          {card.header.subtitle && (
            <p style={{
              fontSize: '0.9rem',
              color: textColorLight,
              margin: '4px 0 0 0',
              fontStyle: 'italic'
            }}>
              {card.header.subtitle}
            </p>
          )}
        </div>
      )}
      
      {card.body && (
        <div>
          {card.body.text && (
            <div 
              style={{ 
                lineHeight: 1.7, 
                marginBottom: '16px',
                color: textColor
              }}
              dangerouslySetInnerHTML={{ __html: parseMarkdown(card.body.text) }}
            />
          )}
          
          {card.body.list && (
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: '16px 0' 
            }}>
              {card.body.list.map((item, idx) => (
                <li key={idx} style={{ 
                  padding: '8px 0 8px 24px', 
                  position: 'relative',
                  lineHeight: 1.6,
                  color: textColorLight
                }}>
                  <span style={{ 
                    position: 'absolute', 
                    left: 0, 
                    color: hasGradientBg ? COLORS.accentGold : COLORS.accentGold,
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    ▸
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: parseMarkdown(item) }} />
                </li>
              ))}
            </ul>
          )}

          {/* Badge Grid for comparison cards */}
          {card.body.badges && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginTop: '12px'
            }}>
              {card.body.badges.map((badge, idx) => {
                const badgeColors = {
                  danger: { bg: '#dc3545', text: COLORS.white },
                  success: { bg: COLORS.successGreen, text: COLORS.white },
                  warning: { bg: COLORS.accentGold, text: COLORS.navyPrimary },
                  info: { bg: COLORS.lightNavy, text: COLORS.white }
                };
                const colors = badgeColors[badge.variant] || badgeColors.info;
                
                return (
                  <span key={idx} style={{
                    background: colors.bg,
                    color: colors.text,
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    {badge.text}
                  </span>
                );
              })}
            </div>
          )}
          
          {card.body.sections && card.body.sections.map((section, idx) => (
            <div key={idx} style={{ marginTop: '16px' }}>
              <RenderComponent component={section} />
            </div>
          ))}
          
          {card.body.link && (
            <div style={{ marginTop: '16px' }}>
              <a
                href={card.body.link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  background: hasGradientBg ? COLORS.white : COLORS.navyPrimary,
                  color: hasGradientBg ? COLORS.navyPrimary : COLORS.white,
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {card.body.link.text} →
              </a>
            </div>
          )}
          
          {card.body.metric && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: 700,
                color: styling.color || (hasGradientBg ? COLORS.white : COLORS.navyPrimary),
                letterSpacing: '-0.02em'
              }}>
                {card.body.metric.value}
              </div>
              <div style={{ 
                fontSize: '1rem', 
                color: styling.color || (hasGradientBg ? COLORS.accentGold : COLORS.textLight),
                marginTop: '4px'
              }}>
                {card.body.metric.label}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// TextBlock Component - inherits color from parent
const TextBlock = ({ data }) => {
  const content = data.content || {};
  
  return (
    <div 
      style={{ 
        lineHeight: 1.7, 
        fontSize: '1rem',
        // Inherit color from parent to work in dark sections
        color: 'inherit',
        margin: '1rem 0'
      }}
      dangerouslySetInnerHTML={{ __html: parseMarkdown(content.text || '') }}
    />
  );
};

// List Component
const List = ({ data }) => {
  const content = data.content || {};
  const variant = data.variant || 'bullet';
  
  const ListTag = variant === 'numbered' ? 'ol' : 'ul';
  
  return (
    <ListTag style={{ 
      padding: variant === 'numbered' ? '0 0 0 24px' : 0,
      margin: '16px 0',
      listStyle: variant === 'numbered' ? 'decimal' : 'none'
    }}>
      {content.items && content.items.map((item, idx) => (
        <li key={idx} style={{ 
          padding: variant === 'numbered' ? '8px 0' : '8px 0 8px 24px', 
          position: 'relative',
          lineHeight: 1.6,
          color: COLORS.textLight // Original color for regular sections; CSS !important overrides in dark sections
        }}>
          {variant !== 'numbered' && (
            <span style={{ 
              position: 'absolute', 
              left: 0, 
              color: variant === 'checklist' ? COLORS.successGreen : COLORS.accentGold,
              fontWeight: 'bold',
              fontSize: '1.1rem'
            }}>
              {variant === 'checklist' ? '✓' : '▸'}
            </span>
          )}
          <span dangerouslySetInnerHTML={{ __html: parseMarkdown(item.text || item) }} />
        </li>
      ))}
    </ListTag>
  );
};

// Simple markdown parser with table support
function parseMarkdown(text) {
  if (!text) return '';
  
  // Check if text contains a markdown table
  const lines = text.split('\n');
  const tableStartIndex = lines.findIndex(line => line.trim().startsWith('|') && line.trim().endsWith('|'));
  
  if (tableStartIndex !== -1) {
    // Find table boundaries
    let tableEndIndex = tableStartIndex;
    for (let i = tableStartIndex; i < lines.length; i++) {
      if (lines[i].trim().startsWith('|')) {
        tableEndIndex = i;
      } else {
        break;
      }
    }
    
    // Extract table lines
    const beforeTable = lines.slice(0, tableStartIndex).join('\n');
    const tableLines = lines.slice(tableStartIndex, tableEndIndex + 1);
    const afterTable = lines.slice(tableEndIndex + 1).join('\n');
    
    // Parse table
    const tableHtml = parseMarkdownTable(tableLines);
    
    // Recursively parse before and after
    const beforeHtml = beforeTable ? parseMarkdown(beforeTable) : '';
    const afterHtml = afterTable ? parseMarkdown(afterTable) : '';
    
    return beforeHtml + tableHtml + afterHtml;
  }
  
  // Standard markdown parsing
  return text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: ' + COLORS.navyPrimary + '; text-decoration: underline; font-weight: 500;">$1</a>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
}

// Parse markdown table to HTML
function parseMarkdownTable(lines) {
  if (lines.length < 2) return '';
  
  // Filter out separator line (contains only |, -, and spaces)
  const dataLines = lines.filter(line => !line.match(/^\|[\s\-:|]+\|$/));
  
  if (dataLines.length === 0) return '';
  
  // Parse header
  const headerCells = dataLines[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim());
  
  // Parse body rows
  const bodyRows = dataLines.slice(1).map(line => 
    line.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
  );
  
  // Build HTML table
  let html = '<table class="json-table">';
  
  // Header
  html += '<thead><tr>';
  headerCells.forEach(cell => {
    html += `<th>${parseInlineMarkdown(cell)}</th>`;
  });
  html += '</tr></thead>';
  
  // Body
  html += '<tbody>';
  bodyRows.forEach(row => {
    html += '<tr>';
    row.forEach(cell => {
      html += `<td>${parseInlineMarkdown(cell)}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table>';
  
  return html;
}

// Parse inline markdown (bold, italic, code) without block elements
function parseInlineMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');
}

// CSS Styles with new color palette
function getStyles() {
  return `
    .json-content-renderer {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: ${COLORS.text};
      padding: 24px;
      background: ${COLORS.background};
      min-height: 100%;
    }
    
    .json-content-renderer strong {
      color: ${COLORS.accentGold};
      font-weight: 600;
    }
    
    /* Dark section text contrast - ensure all text is white */
    .json-section-dark {
      color: ${COLORS.white} !important;
    }
    
    
    .json-section-dark strong,
    .json-section-dark b {
      color: ${COLORS.accentGold} !important;
      font-weight: 600;
    }
    
    .json-section-dark em,
    .json-section-dark i {
      color: rgba(255, 255, 255, 0.95) !important;
    }
    
    .json-section-dark a {
      color: ${COLORS.accentGold} !important;
      text-decoration: underline;
    }
    
    .json-section-dark a:hover {
      color: ${COLORS.goldDark} !important;
    }
    
    .json-section-dark code {
      background: rgba(255, 255, 255, 0.2) !important;
      color: ${COLORS.white} !important;
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .json-section-dark li {
      color: ${COLORS.white} !important;
    }
    
    .json-section-dark ul li::before,
    .json-section-dark ol li::before {
      color: ${COLORS.accentGold} !important;
    }
    
    /* Dark callout text - ensure all text is visible */
    .json-callout-dark,
    .json-callout-dark p,
    .json-callout-dark li,
    .json-callout-dark span,
    .json-callout-dark div {
      color: #ffffff !important;
    }
    
    .json-callout-dark strong {
      color: ${COLORS.accentGold} !important;
    }
    
    .json-callout-dark em {
      color: #ffffff !important;
      font-style: italic;
    }
    
    .json-callout-dark code {
      background: rgba(255,255,255,0.15);
      color: ${COLORS.accentGold} !important;
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    /* Table styling */
    .json-table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
      background: ${COLORS.white};
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(26, 58, 82, 0.1);
      font-size: 0.95rem;
    }
    
    .json-table th {
      background: linear-gradient(135deg, ${COLORS.navyPrimary}, ${COLORS.lightNavy});
      color: ${COLORS.white};
      font-weight: 600;
      padding: 12px 16px;
      text-align: left;
      border-bottom: 2px solid ${COLORS.lightNavy};
    }
    
    .json-table td {
      padding: 12px 16px;
      border-bottom: 1px solid ${COLORS.border};
      color: ${COLORS.text};
    }
    
    .json-table tbody tr:hover {
      background: rgba(45, 95, 126, 0.05);
    }
    
    .json-table tbody tr:last-child td {
      border-bottom: none;
    }
    
    .json-table code {
      background: rgba(212, 165, 116, 0.15);
      color: ${COLORS.goldDark};
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 0.9em;
    }
    
    .json-table strong {
      color: ${COLORS.navyPrimary};
    }
    
    /* Light callout variants - better contrast for bold text */
    .json-callout-light-danger strong {
      color: ${COLORS.dangerRed} !important;
    }
    
    .json-callout-light-danger code {
      background: rgba(192, 57, 43, 0.12);
      color: ${COLORS.dangerRed};
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .json-callout-light-warning strong {
      color: ${COLORS.goldDark} !important;
    }
    
    .json-callout-light-warning code {
      background: rgba(192, 143, 92, 0.15);
      color: ${COLORS.goldDark};
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .json-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(26, 58, 82, 0.12) !important;
    }
    
    /* Gradient card text contrast - default to white, but allow custom colors via inline styles */
    .json-card-gradient {
      color: ${COLORS.white};
    }
    
    .json-card-gradient * {
      color: ${COLORS.white};
    }
    
    /* Strong/bold text in gradient cards - keep white for contrast unless overridden */
    .json-card-gradient strong,
    .json-card-gradient b {
      color: ${COLORS.white};
      font-weight: 600;
    }
    
    .json-card-gradient em,
    .json-card-gradient i {
      color: rgba(255, 255, 255, 0.95);
    }
    
    .json-card-gradient a {
      color: ${COLORS.accentGold} !important;
      text-decoration: underline;
    }
    
    .json-card-gradient a:hover {
      color: ${COLORS.goldDark} !important;
    }
    
    .json-card-gradient code {
      background: rgba(255, 255, 255, 0.2) !important;
      color: ${COLORS.white} !important;
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .json-hero:hover {
      box-shadow: 0 12px 40px rgba(26, 58, 82, 0.3);
    }
    
    /* Improve readability */
    .json-content-renderer p {
      margin: 0 0 12px 0;
    }
    
    .json-content-renderer p:last-child {
      margin-bottom: 0;
    }
    
    /* Scrollbar styling */
    .json-content-renderer::-webkit-scrollbar {
      width: 8px;
    }
    
    .json-content-renderer::-webkit-scrollbar-track {
      background: ${COLORS.background};
    }
    
    .json-content-renderer::-webkit-scrollbar-thumb {
      background: ${COLORS.border};
      border-radius: 4px;
    }
    
    .json-content-renderer::-webkit-scrollbar-thumb:hover {
      background: ${COLORS.textLight};
    }
    
    @media (max-width: 768px) {
      .json-content-renderer {
        padding: 16px;
      }
      
      .json-card {
        padding: 20px !important;
      }
    }
  `;
}

export default JsonContentRenderer;
